import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import Home from '@/views/Home.vue';
import Router, { Route } from 'vue-router';
import { NavigationGuardNext } from 'vue-router/types/router';
import { IReport } from '@/classes/TechnicalResultsInterface';

interface Dependencies {
  getScenario: () => ScenarioExt;
  getReport: () => IReport | null;
  hasTechnicalReport: () => boolean;
  loadPresets: () => Promise<unknown>;
}

export enum ROUTES {
  home = 'home',
  scenario_step_wizard = 'scenario_step_wizard',
  scenario_step_pv = 'scenario_step_pv',
  scenario_step_load = 'scenario_step_load',
  scenario_step_grid = 'scenario_step_grid',
  scenario_step_generator = 'scenario_step_generator',
  scenario_step_battery = 'scenario_step_battery',
  scenario_step_eco = 'scenario_step_eco',
  scenario_step_sizing = 'scenario_step_sizing',
  scenario_technical_report = 'scenario_technical_report',
  scenario_economical_report = 'scenario_economical_report',
  scenario_client_result = 'scenario_client_result',
  scenario_executive_report = 'scenario_executive_report',
  scenario_step_dev = 'scenario_step_dev',
  // for ren_fr workflow
  scenario_ren_fr_step_pv = 'scenario_ren_fr_step_pv',
  scenario_ren_fr_step_wind = 'scenario_ren_fr_step_wind',
  scenario_ren_fr_step_revenue = 'scenario_ren_fr_step_revenue',
  scenario_ren_fr_step_capex = 'scenario_ren_fr_step_capex',
  scenario_ren_fr_step_opex = 'scenario_ren_fr_step_opex',
  scenario_ren_fr_step_structure_finance = 'scenario_ren_fr_step_structure_finance',
  scenario_ren_fr_economical_report = 'scenario_ren_fr_economical_report',
}

export const buildRouter = (dependencies: Dependencies): Router => {
  const {
    getScenario,
    getReport,
    hasTechnicalReport,
    loadPresets
  } = dependencies;

  const checkThereIsScenarioWithId = (to: Route, from: Route, next: NavigationGuardNext): void => {
    const scenario = getScenario();
    if (!scenario.uuid) {
      next({ name: ROUTES.home });
    } else {
      next();
    }
  };

  const checkIfHasReportOrScenarioWithId = (to: Route, from: Route, next: NavigationGuardNext): void => {
    const report = getReport();
    if (report) {
      next();
    } else {
      next({ name: ROUTES.scenario_step_sizing });
    }
  };

  const checkTechnicalReportAccess = (to: Route, from: Route, next: NavigationGuardNext): void => {
    const hasAccess = hasTechnicalReport();
    if (hasAccess) {
      checkIfHasReportOrScenarioWithId(to, from, next);
    } else {
      next({ name: ROUTES.scenario_economical_report });
    }
  };

  return new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
      {
        path: '',
        name: ROUTES.home,
        component: Home,
        beforeEnter: async(to, from, next) => {
          await loadPresets();
          return next();
        }
      },
      {
        path: '/scenario_step_wizard',
        name: ROUTES.scenario_step_wizard,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Wizard.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_pv',
        name: ROUTES.scenario_step_pv,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/PV.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_load',
        name: ROUTES.scenario_step_load,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Load.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_grid',
        name: ROUTES.scenario_step_grid,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Grid.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_generator',
        name: ROUTES.scenario_step_generator,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Genset.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_battery',
        name: ROUTES.scenario_step_battery,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Battery.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_eco',
        name: ROUTES.scenario_step_eco,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Eco.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_step_sizing',
        name: ROUTES.scenario_step_sizing,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "scenario" */ '@/views/Sizing.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_technical_report',
        name: ROUTES.scenario_technical_report,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "report" */ '@/views/TechnicalReport.vue'),
        beforeEnter: checkTechnicalReportAccess
      },
      {
        path: '/scenario_economical_report',
        name: ROUTES.scenario_economical_report,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/EconomicalReport.vue'),
        beforeEnter: checkIfHasReportOrScenarioWithId
      },
      {
        path: '/scenario_client_result',
        name: ROUTES.scenario_client_result,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ClientResult.vue'),
        beforeEnter: checkIfHasReportOrScenarioWithId
      },
      {
        path: '/scenario_executive_report/:uuid?',
        name: ROUTES.scenario_executive_report,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "report" */ '@/views/ExecutiveReport.vue'),
        beforeEnter: checkIfHasReportOrScenarioWithId
      },
      {
        path: '/scenario_step_dev',
        name: ROUTES.scenario_step_dev,
        // route level code-splitting
        // this generates a separate chunk ([name].[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "dev" */ '@/views/Dev.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      // for ren_fr workflow
      {
        path: '/scenario_ren_fr_step_pv',
        name: ROUTES.scenario_ren_fr_step_pv,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/PV.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_step_wind',
        name: ROUTES.scenario_ren_fr_step_wind,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/Wind.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_step_revenue',
        name: ROUTES.scenario_ren_fr_step_revenue,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/Revenue.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_step_capex',
        name: ROUTES.scenario_ren_fr_step_capex,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/Capex.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_step_opex',
        name: ROUTES.scenario_ren_fr_step_opex,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/Opex.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_step_structure_finance',
        name: ROUTES.scenario_ren_fr_step_structure_finance,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/StructureFinance.vue'),
        beforeEnter: checkThereIsScenarioWithId
      },
      {
        path: '/scenario_ren_fr_economical_report',
        name: ROUTES.scenario_ren_fr_economical_report,
        component: () =>
          import(/* webpackChunkName: "report" */ '@/views/ren-fr/EconomicalReport.vue'),
        beforeEnter: checkIfHasReportOrScenarioWithId
      },
      {
        path: '*',
        redirect: '/'
      }
    ]
  });
};
